import React from 'react'
import { xor, union, difference, merge, cloneDeep, set } from 'lodash'
import styled from 'styled-components'
import * as Options from './Options.js'
import Map from './Map/index.js'
import Roster from './Roster/index.js'
import TimeTracker from './Time/TimeTracker.js'
import TimeSlider from './Time/TimeSlider.js'
import AutoplayControls from './Time/AutoplayControls.js'
import MatchInfo from './MatchInfo.js'
import HelpModal from './HelpModal.js'
import DownloadButton from './DownloadButton.js'

const MAP_SIZES = {
    Erangel_Main: 816000,
    Baltic_Main: 816000,
    Desert_Main: 816000,
    Savage_Main: 408000,
    DihorOtok_Main: 816000,
    Summerland_Main: 204000,
    Chimera_Main: 306000,
    Heaven_Main: 102000,
    Tiger_Main: 816000,
    Kiki_Main: 816000,
    Neon_Main: 816000,
}

const getDefaultAreaOverlayPosition = (match) => {
    const size = (match && match.mapName ? MAP_SIZES[match.mapName] : null) || 816000
    const c = size / 2
    return { x: c, y: c }
}

// -----------------------------------------------------------------------------
// Styled Components -----------------------------------------------------------
// -----------------------------------------------------------------------------

const MatchContainer = styled.div`
    display: grid;
    grid-template-columns: 1fr 280px;
    grid-column-gap: 10px;
    border: 0px solid #eee;
    overflow: visible;
    margin: 0 auto;
    max-width: calc(100vh + 290px);

    @media (max-width: 700px) {
        grid-template-columns: 1fr 0px;
        grid-column-gap: 0;
        grid-row-gap: 15px;
    }
`

const MapContainer = styled.div`
    grid-column: 1;
    position: relative;
    cursor: ${props => props.isDotHovered ? 'pointer' : 'normal'};
    display: grid;
`

const MapWithSidebar = styled.div`
    display: grid;
    grid-template-columns: 1fr 48px;
    grid-column-gap: 6px;
    grid-column: 1;
`

const MapOptionsPanel = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: center;
    padding-top: 8px;
`

const MapOptionButton = styled.button`
    width: 42px;
    min-height: 42px;
    padding: 0;
    border: 1px solid var(--border);
    border-radius: 8px;
    background: var(--bg-elevated);
    color: var(--grey-2);
    font-size: 1.75rem;
    font-weight: normal;
    cursor: pointer;
    text-align: center;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;

    i {
        font-size: inherit;
    }

    &:hover {
        background: var(--grey-0);
        color: var(--accent);
    }

    ${p => p.$active && `
        background: rgba(245, 158, 11, 0.15);
        border-color: var(--accent);
        color: var(--accent);
    `}
`

const AreaNumberRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    justify-content: center;
    max-width: 42px;
`

const AreaNumberButton = styled.button`
    width: 18px;
    height: 18px;
    padding: 0;
    border: 1px solid var(--border);
    border-radius: 4px;
    background: var(--bg-elevated);
    color: var(--grey-2);
    font-size: 0.65rem;
    font-weight: 600;
    cursor: pointer;
    line-height: 1;

    &:hover {
        background: var(--grey-0);
        color: #E63946;
    }

    ${p => p.$active && `
        background: rgba(230, 57, 70, 0.2);
        border-color: #E63946;
        color: #E63946;
    `}
`

const RosterContainer = styled.div`
    grid-column: 2;
    min-width: 0;
    overflow-y: scroll;
    overflow-x: auto;
    height: ${props => props.mapSize + 48}px;
    padding-right: 10px;
    scrollbar-color: var(--grey-0) var(--bg);

    &::-webkit-scrollbar {
      width: 10px;
      background: none;
    }

    &::-webkit-scrollbar-thumb {
        background: var(--grey-0);
        border-radius: 4px;
    }

    @media (max-width: 700px) {
        grid-column: 1;
        grid-row: 2;
    }
`

const MatchHeader = styled.div`
    display: grid;
    grid-template-columns: max-content 1fr max-content;
    margin-bottom: 10px;

    @media (max-width: 700px) {
        grid-template-columns: 0px 1fr max-content;
        grid-row: 2;
        margin-top: 10px;
        margin-bottom: 0;
    }
`

class MatchPlayer extends React.Component {
    constructor(props) {
        super(props)

        this.state = {
            mapSize: 0,
            focusedPlayer: props.playerName,
            // See getDerivedStateFromProps
            prevPlayerName: props.playerName,
            hoveredPlayer: null,
            trackedPlayers: [],
            options: Options.DEFAULT_OPTIONS,
            setOption: null,
            showDeadPlayers: true,
            showAllNames: false,
            showAreaOverlays: false,
            areaOverlayPosition: null,
            selectedAreaNumber: 1,
            showPlanePath: true,
        }
    }

    marks = {
        focusedPlayer: () => this.state.focusedPlayer,
        isPlayerFocused: playerName => this.state.focusedPlayer === playerName,

        hoveredPlayer: () => this.state.hoveredPlayer,
        isPlayerHovered: playerName => this.state.hoveredPlayer === playerName,
        setHoveredPlayer: playerName => this.setState({ hoveredPlayer: playerName }),

        trackedPlayers: () => this.state.trackedPlayers,
        isPlayerTracked: playerName => this.state.trackedPlayers.includes(playerName),
        toggleTrackedPlayer: (...playerNames) => {
            this.setState(({ trackedPlayers }) => {
                if (playerNames.length > 1 && difference(playerNames, trackedPlayers).length !== 0) {
                    return {
                        trackedPlayers: union(trackedPlayers, playerNames),
                    }
                }

                return {
                    trackedPlayers: xor(trackedPlayers, playerNames),
                }
            })
        },
        setTrackedPlayers: (playerNames) => this.setState({ trackedPlayers: Array.isArray(playerNames) ? playerNames : [] }),
    }

    setAreaOverlayPosition = (x, y) => {
        this.setState({ areaOverlayPosition: { x, y } })
    }

    // -------------------------------------------------------------------------
    // Map Sizing, Lifecycle ---------------------------------------------------
    // -------------------------------------------------------------------------

    // https://reactjs.org/blog/2018/06/07/you-probably-dont-need-derived-state.html
    // HACK-ish. Should probably turn this into a controlled component.
    // The functionality isn't needed right now, but I'd rather not break it.
    static getDerivedStateFromProps(nextProps, prevState) {
        if (prevState.prevPlayerName !== nextProps.playerName) {
            return {
                focusedPlayer: nextProps.playerName,
                prevPlayerName: nextProps.playerName,
            }
        }

        return null
    }

    componentDidMount() {
        window.addEventListener('resize', this.updateMapSize.bind(this))
        this.updateMapSize()
        this.loadOptions()
    }

    componentDidUpdate(prevProps, prevState) {
        this.updateMapSize()
    }

    loadOptions = () => {
        const localOptions = JSON.parse(localStorage.getItem(Options.STORAGE_KEY) || '{}')
        const options = merge(Options.DEFAULT_OPTIONS, localOptions)

        const setOption = (key, val) => {
            this.setState(prevState => {
                const newOptions = cloneDeep(prevState.options)
                set(newOptions, key, val)
                localStorage.setItem(Options.STORAGE_KEY, JSON.stringify(newOptions))
                return { options: newOptions }
            })
        }

        this.setState({ options, setOption })
    }

    updateMapSize = () => {
        const stageWrapper = document.getElementById('StageWrapper')

        if (stageWrapper) {
            this.setState(ps => {
                if (ps.mapSize !== stageWrapper.clientWidth) {
                    return { mapSize: stageWrapper.clientWidth }
                }

                return null
            })
        }
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.updateMapSize.bind(this))
    }

    // -------------------------------------------------------------------------
    // Render ------------------------------------------------------------------
    // -------------------------------------------------------------------------

    render() {
        const { match, rawTelemetry, telemetry, rosters, globalState, playerNames } = this.props
        const { mapSize, options, setOption, prevPlayerName } = this.state

        return (
            <Options.Context.Provider value={{ options, setOption }}>
                <TimeTracker
                    options={options}
                    durationSeconds={match.durationSeconds + 5}
                    telemetry={telemetry}
                    render={({ msSinceEpoch, timeControls, currentTelemetry }) =>
                        <MatchContainer id="MatchContainer">
                            <MapContainer id="MapContainer" isDotHovered={!!this.marks.hoveredPlayer()}>
                                <MatchHeader>
                                    <MatchInfo
                                        match={match}
                                        marks={this.marks}
                                        rawTelemetry={rawTelemetry}
                                        playerName={prevPlayerName}
                                        circlePhase={currentTelemetry && currentTelemetry.circlePhase}
                                    />
                                    <TimeSlider
                                        value={msSinceEpoch}
                                        stopAutoplay={timeControls.stopAutoplay}
                                        onChange={timeControls.setMsSinceEpoch}
                                        durationSeconds={match.durationSeconds + 5}
                                        globalState={globalState}
                                        options={options}
                                    />
                                    <AutoplayControls
                                        autoplay={timeControls.autoplay}
                                        autoplaySpeed={timeControls.autoplaySpeed}
                                        toggleAutoplay={timeControls.toggleAutoplay}
                                        changeSpeed={timeControls.setAutoplaySpeed}
                                        isFinished={(match.durationSeconds + 5) === (msSinceEpoch / 1000)}
                                        rewindToStart={timeControls.rewindToStart}
                                    />
                                </MatchHeader>
                                <MapWithSidebar>
                                    <Map
                                        match={match}
                                        telemetry={currentTelemetry}
                                        mapSize={mapSize}
                                        marks={this.marks}
                                        msSinceEpoch={msSinceEpoch}
                                        options={options}
                                        playerNames={playerNames}
                                        rosters={rosters}
                                        showDeadPlayers={this.state.showDeadPlayers}
                                        showAllNames={this.state.showAllNames}
                                        showAreaOverlays={this.state.showAreaOverlays}
                                        areaOverlayPosition={this.state.areaOverlayPosition}
                                        selectedAreaNumber={this.state.selectedAreaNumber}
                                        phaseRadii={(this.props.telemetry && this.props.telemetry.phaseRadii) || {}}
                                        planePath={this.state.showPlanePath ? (this.props.globalState && this.props.globalState.planePath) : null}
                                        onAreaOverlayPositionChange={this.setAreaOverlayPosition}
                                    />
                                    <MapOptionsPanel>
                                        <MapOptionButton
                                            type="button"
                                            $active={this.state.showDeadPlayers}
                                            onClick={() => this.setState(s => ({ showDeadPlayers: !s.showDeadPlayers }))}
                                            title={this.state.showDeadPlayers ? 'Ölü oyuncuları gizle' : 'Ölü oyuncuları göster'}
                                        >
                                            <i className="fi-x-circle" />
                                        </MapOptionButton>
                                        <MapOptionButton
                                            type="button"
                                            $active={this.state.showAllNames}
                                            onClick={() => this.setState(s => ({ showAllNames: !s.showAllNames }))}
                                            title={this.state.showAllNames ? 'Tüm isimleri gizle' : 'Tüm isimleri göster'}
                                        >
                                            <i className="fi-list" />
                                        </MapOptionButton>
                                        <MapOptionButton
                                            type="button"
                                            $active={this.state.showAreaOverlays}
                                            onClick={() => this.setState(s => ({
                                                showAreaOverlays: !s.showAreaOverlays,
                                                areaOverlayPosition: s.areaOverlayPosition != null
                                                    ? s.areaOverlayPosition
                                                    : getDefaultAreaOverlayPosition(this.props.match),
                                            }))}
                                            title={this.state.showAreaOverlays ? 'Deneme alanını gizle' : 'Deneme alanı ekle (1.-8. seçip sürükleyerek konumlandır)'}
                                        >
                                            <i className="fi-marker" />
                                        </MapOptionButton>
                                        <MapOptionButton
                                            type="button"
                                            $active={this.state.showPlanePath}
                                            onClick={() => this.setState(s => ({ showPlanePath: !s.showPlanePath }))}
                                            title={this.state.showPlanePath ? 'Uçak rotasını gizle' : 'Uçak rotasını göster'}
                                        >
                                            <i className="fi-compass" />
                                        </MapOptionButton>
                                        {this.state.showAreaOverlays && (
                                            <AreaNumberRow>
                                                {[1, 2, 3, 4, 5, 6, 7, 8].map(n => (
                                                    <AreaNumberButton
                                                        key={n}
                                                        type="button"
                                                        $active={this.state.selectedAreaNumber === n}
                                                        onClick={() => this.setState({ selectedAreaNumber: n })}
                                                        title={'Alan ' + n}
                                                    >
                                                        {n}
                                                    </AreaNumberButton>
                                                ))}
                                            </AreaNumberRow>
                                        )}
                                    </MapOptionsPanel>
                                </MapWithSidebar>
                                <HelpModal mapSize={mapSize} />
                                {rawTelemetry && (
                                    <DownloadButton
                                        match={match}
                                        playerName={prevPlayerName}
                                        rawTelemetry={rawTelemetry}
                                    />
                                )}
                            </MapContainer>
                            <RosterContainer mapSize={mapSize}>
                                <Roster
                                    match={match}
                                    telemetry={currentTelemetry}
                                    rosters={rosters}
                                    marks={this.marks}
                                />
                            </RosterContainer>
                        </MatchContainer>
                    }
                />
            </Options.Context.Provider>
        )
    }
}

export default MatchPlayer



// WEBPACK FOOTER //
// ./src/components/MatchPlayer/index.js